import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Coffee, Calendar, MapPin, ChevronDown, Star, ArrowRight } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SectionHeader } from '../components/SectionHeader';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_sandbar-coffee/artifacts/c3buqejl_Larger%20SBC%20Logo%20transparent.png";

// Services section icon badges (locally hosted, true-transparent PNGs)
const SERVICE_ICONS = {
  eventService: `${process.env.PUBLIC_URL}/services/event-service.png?v=2`,
  partnership: `${process.env.PUBLIC_URL}/services/partnership.png?v=2`,
  bottledLatte: `${process.env.PUBLIC_URL}/services/bottled-latte.png?v=2`
};

// Event card background images
const EVENT_IMAGES = {
  corporate: "https://customer-assets.emergentagent.com/job_sandbar-coffee/artifacts/rzzy7ofo_827dd7f3-ea17-4038-820d-fdbf1b1ee073.jpg",
  private: "https://customer-assets.emergentagent.com/job_beach-coffee/artifacts/sp5gjvyi_IMG_7302.jpeg",
  popups: "https://customer-assets.emergentagent.com/job_sandbar-coffee/artifacts/621jx02b_4cacfaed-5623-4967-8434-051960e6c268.jpg"
};

const REVIEWS = [
  {
    name: "Breanne A.",
    text: "Sandbar Coffee is where quality is no question. Every cup is handcrafted with care and intention. You can really feel the love he puts into every drink. My top picks are the matcha and the rum cake latte. A mobile espresso bar that brings community, conversation, and the energy we needed in Nassau. Believe the hype!",
    stars: 5
  },
  {
    name: "Abby R.",
    text: "Some of the best coffee on the island. Patiently waiting for a brick and mortar location!",
    stars: 5
  },
  {
    name: "Holly S.",
    text: "This is what The Bahamas needs more of: quality, bespoke, carefully curated coffee! Lauren creates amazing vibes surrounding his coffee brand and it is truly the best coffee on the island. Highly recommend booking for your next event!",
    stars: 5
  },
  {
    name: "Laura M.",
    text: "Sandbar Coffee did a pop up event at Morley Realty on Shirley Street and it was a huge hit. Lauren delivered excellent coffee, a professional setup, and friendly, efficient service. Great product all around — we'd absolutely have him back again.",
    stars: 5
  },
  {
    name: "Britt C.",
    text: "Best coffee in Nassau! Guava Matcha was super good - lovely subtle flavors. Cinnamon Latte also super good! Lauren is awesome! Very knowledgeable and takes pride in his product and his work. Highly recommend!",
    stars: 5
  }
];

const Home = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [currentReview, setCurrentReview] = useState(0);

  // Rotate reviews every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentReview((prev) => (prev + 1) % REVIEWS.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "How do I book?",
      answer: "Fill out our event interest form below, and we will try to get a quote back to you within 24 hours. We will then confirm your booking once we have received your full payment."
    },
    {
      question: "What are the terms for booking?",
      answer: "Full payment required before service begins. Please book two weeks in advance to ensure availablility and proper preparation."
    },
    {
      question: "What is your cancellation and rescheduling policy",
      answer: "We understand that plans can change. Cancellations made more than seven (7) days prior to the scheduled event will receive a 50% refund of the total payment. Cancellations made within seven (7) days of the event are non-refundable. If rescheduling is needed, we're happy to apply your payment toward a future event date, pending availability."
    },
    {
      question: "What's included?",
      answer: "We come fully equipped with the espresso machine, grinder, syrups, two milks (whole and oat), ice, and all supplies needed to serve your guests. You provide the power and the space!"
    },
    {
      question: "Do you need power?",
      answer: "Yes - we need at least two dedicated 15A/120V outlets within 25ft. If power isn't available, we can bring a power station (~4hr runtime) for an additional $100 flat fee."
    },
    {
      question: "How much space do you need?",
      answer: "A flat, shaded area (if outside) at least 6x6ft works best. We bring the cart, coolers, and everything necessary to get up and running."
    },
    {
      question: "What about weather?",
      answer: "For outdoor setups, covered or indoor space is required to protect our equipment. Strong wind can affect espresso quality and rain can jeopardize our equipment. In cases of severe weather, service may need to be moved indoors or rescheduled."
    },
    {
      question: "What do you need before our event?",
      answer: "Confirmation of power & space access, ensure parking, and reasonable loading access (no stairs please), and having a clear setup area 1 hour before start time."
    }
  ];

  return (
    <div className="home-page">
      <Header />

      {/* Hero Section - Split Layout */}
      <section id="home" className="hero-section">
        <div className="hero-content-split">
          <div className="hero-text-column">
            <img src={LOGO_URL} alt="Sandbar Coffee" className="hero-logo" />
            <div className="hero-announcement">
              <Coffee size={14} />
              <span>Mobile Espresso Bar • Nassau, Bahamas</span>
            </div>
            <h1 className="heading-hero hero-title">
              Where Calm Meets Caffeine
            </h1>
            <p className="body-large hero-subtitle">
              A mobile espresso bar serving specialty coffee and matcha beverages across Nassau. From office pop-ups to weddings, we handle setup, service, and everything in between.
            </p>
            <div className="hero-buttons">
              <Link to="/book" className="btn-primary" data-testid="hero-book-event-btn">Get a Quote</Link>
              <a href="#faq" className="btn-secondary">Learn More</a>
            </div>
          </div>
          <div className="hero-image-column">
            <img
              src="https://customer-assets.emergentagent.com/job_sandbar-coffee/artifacts/tfgrws6j_a53848fa-ab57-4375-92e8-6abc7ec34a56.jpg"
              alt="Sandbar Coffee Founder"
              className="hero-profile-image"
            />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section">
        <SectionHeader
          eyebrow="Our Story"
          title="About Sandbar Coffee"
          subtitle="Bringing LA coffee culture to the islands"
        />
        <div className="about-grid">
          {/* Photo on left */}
          <div className="about-photo-container">
            <img
              src="https://customer-assets.emergentagent.com/job_beach-coffee/artifacts/lsz029cd_IMG_7947.jpeg"
              alt="Sandbar Coffee Cart Setup"
              className="about-hero-photo"
            />
          </div>
          {/* Card on right */}
          <div className="card">
            <h3 className="heading-2" style={{ marginBottom: '1rem' }}>Our Story</h3>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
              Born from a love of quality coffee and the laid-back rhythm of island life, Sandbar Coffee brings the creativity, culture, and craft of the LA coffee scene home to The Bahamas.
            </p>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
              From office pop-ups to weddings, we handle setup, service, and everything in between—bringing café-quality experiences wherever you are.
            </p>
            <h4 className="body-medium" style={{ fontWeight: 600, marginBottom: '1rem', color: 'var(--brand-blue)' }}>
              What Makes Us Different
            </h4>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <li className="body-medium" style={{ marginBottom: '0.75rem', paddingLeft: '1.5rem', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0, color: 'var(--brand-blue)', fontWeight: 600 }}>✓</span>
                Single origin espresso courtesy of Cielito Coffee
              </li>
              <li className="body-medium" style={{ marginBottom: '0.75rem', paddingLeft: '1.5rem', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0, color: 'var(--brand-blue)', fontWeight: 600 }}>✓</span>
                Café-quality service
              </li>
              <li className="body-medium" style={{ marginBottom: '0.75rem', paddingLeft: '1.5rem', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0, color: 'var(--brand-blue)', fontWeight: 600 }}>✓</span>
                House-made syrups
              </li>
              <li className="body-medium" style={{ paddingLeft: '1.5rem', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0, color: 'var(--brand-blue)', fontWeight: 600 }}>✓</span>
                Island-inspired specialty drinks
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <SectionHeader eyebrow="Testimonials" title="What People Are Saying">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
            <div style={{ display: 'flex', gap: '0.2rem' }}>
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={18} fill="var(--brand-blue)" color="var(--brand-blue)" />
              ))}
            </div>
            <span className="body-medium" style={{ color: 'var(--text-muted)' }}>5.0 on Google</span>
          </div>
        </SectionHeader>
        <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
          <p className="body-large" style={{
            fontStyle: 'italic',
            color: 'var(--text-secondary)',
            marginBottom: '1.25rem',
            lineHeight: '1.8'
          }}>
            &ldquo;{REVIEWS[currentReview].text}&rdquo;
          </p>
          <p className="body-medium" style={{
            fontWeight: 600,
            color: 'var(--brand-brown)',
            marginBottom: '1.5rem'
          }}>
            — {REVIEWS[currentReview].name}
          </p>

          {/* Dots */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
            {REVIEWS.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentReview(index)}
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  border: 'none',
                  background: currentReview === index ? 'var(--brand-blue)' : 'var(--border-primary)',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'background 0.3s ease'
                }}
                aria-label={`View review ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section id="services" className="section">
        <SectionHeader
          eyebrow="What We Do"
          title="Who We Cater To"
          subtitle="Mobile espresso bar services tailored to your event"
        />
        <div className="grid-3">
          <div className="card event-card" data-testid="event-card-corporate">
            <div className="event-card-photo" style={{ backgroundImage: `url(${EVENT_IMAGES.corporate})` }} />
            <div className="event-card-body">
              <div className="event-card-icon"><Calendar size={22} /></div>
              <h3 className="heading-2" style={{ marginBottom: '0.5rem' }}>Corporate Events</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Elevate your corporate gatherings, team meetings, and brand activations with professional coffee service that impresses.
              </p>
            </div>
          </div>
          <div className="card event-card" data-testid="event-card-private">
            <div className="event-card-photo" style={{ backgroundImage: `url(${EVENT_IMAGES.private})` }} />
            <div className="event-card-body">
              <div className="event-card-icon"><Coffee size={22} /></div>
              <h3 className="heading-2" style={{ marginBottom: '0.5rem' }}>Private Bookings</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Weddings, birthdays, private parties - we bring the cafe experience to your celebration with custom drink menus.
              </p>
            </div>
          </div>
          <div className="card event-card" data-testid="event-card-popups">
            <div className="event-card-photo" style={{ backgroundImage: `url(${EVENT_IMAGES.popups})` }} />
            <div className="event-card-body">
              <div className="event-card-icon"><MapPin size={22} /></div>
              <h3 className="heading-2" style={{ marginBottom: '0.5rem' }}>Pop-Ups &amp; Activations</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Beach pop-ups, farmer&rsquo;s market or food fests, and special activations. We adapt to any setting while maintaining our quality standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="packages" className="section" style={{ background: 'var(--bg-section)' }}>
        <SectionHeader
          eyebrow="What We Offer"
          title="Services"
          subtitle="Mobile espresso solutions for every occasion"
        />
        <div className="services-rows">
          {/* Event Service */}
          <div className="service-row" data-testid="service-card-event">
            <div className="service-row-media">
              <img src={SERVICE_ICONS.eventService} alt="Event Service" className="service-row-icon" />
            </div>
            <div className="service-row-content">
              <h3 className="heading-2" style={{ marginBottom: '0.75rem' }}>Event Service</h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                The starting point for all bookings — professional setup &amp; service, handcrafted espresso and matcha beverages, oat &amp; whole milk, house-made syrups, and all the cups and supplies your guests need.
              </p>
              <p className="body-small" style={{ color: 'var(--brand-blue)', fontWeight: 600, marginBottom: '1rem' }}>
                Ask us about add-ons!
              </p>
              <Link to="/menu" className="service-link" data-testid="event-see-menu-link">
                See full menu <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          {/* Partnership Program */}
          <div className="service-row" data-testid="service-card-partnership">
            <div className="service-row-media">
              <img src={SERVICE_ICONS.partnership} alt="Partnership Program" className="service-row-icon" />
            </div>
            <div className="service-row-content">
              <h3 className="heading-2" style={{ marginBottom: '0.75rem' }}>Partnership Program</h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                A new subscription-based preferred partnership program helps businesses elevate client experiences and increase profits.
              </p>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                This model provides discounted barista services that can be gifted or marked up for clients—ideal for offices, event planners, hospitality, gyms, and real estate groups.
              </p>
              <p className="body-small" style={{ color: 'var(--brand-blue)', fontWeight: 600 }}>
                Inquire below for partnership pricing.
              </p>
            </div>
          </div>

          {/* Bottled Latte Program */}
          <div className="service-row" data-testid="service-card-bottled">
            <div className="service-row-media">
              <img src={SERVICE_ICONS.bottledLatte} alt="Bottled Latte Program" className="service-row-icon" />
            </div>
            <div className="service-row-content">
              <h3 className="heading-2" style={{ marginBottom: '0.75rem' }}>Bottled Latte Program</h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                Ready to serve 12oz espresso or matcha lattes with your choice of milk and one of 5 Sandbar flavours, delivered directly to you.
              </p>
              <a
                href="https://tally.so/r/A7JGBl"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                data-testid="bottled-latte-order-btn"
              >
                Order Bottled Lattes
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section">
        <SectionHeader
          eyebrow="Good to Know"
          title="Frequently Asked Questions"
          subtitle="Everything you need to know about booking Sandbar Coffee"
        />
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {faqs.map((faq, index) => (
            <div key={index} className={`faq-item ${openFaq === index ? 'open' : ''}`}>
              <div className="faq-question" onClick={() => toggleFaq(index)}>
                <span className="body-medium">{faq.question}</span>
                <ChevronDown
                  size={20}
                  className={`faq-icon ${openFaq === index ? 'open' : ''}`}
                />
              </div>
              {openFaq === index && (
                <div className="faq-answer">
                  <p className="body-small">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Ready to Book CTA Band */}
      <section className="cta-band" data-testid="cta-band">
        <div className="cta-band-inner">
          <h2 className="heading-1" style={{ color: 'white', marginBottom: '0.75rem' }}>Ready for a custom quote?</h2>
          <p className="body-large" style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '1.75rem' }}>
            Tell us about your event and we&rsquo;ll send over a custom quote, usually within 24 hours.
          </p>
          <Link to="/book" className="btn-primary cta-band-btn" data-testid="cta-band-book-btn">
            Get Your Custom Quote
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
